import type { ReactElement } from 'react';
import { Spinner } from 'react-bootstrap';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './auth/AuthContext';
import { CrudScreen } from './crud/CrudScreen';
import { fundTransfersConfig } from './crud/fundTransfers';
import { receiptsConfig } from './crud/receipts';
import { settlementsConfig } from './crud/sellerPayments';
import { purchasesConfig } from './crud/purchases';
import { currenciesConfig } from './crud/currencies';
import { dispatchersConfig } from './crud/dispatchers';
import { sellersConfig } from './crud/sellers';
import { exchangeRatesConfig } from './crud/exchangeRates';
import { banksConfig } from './crud/banks';
import { agentsConfig } from './crud/agents';
import { customersConfig } from './crud/masters';
import { expensesConfig } from './crud/expenses';
import { Layout } from './layout/Layout';
import { DashboardPage } from './pages/Dashboard';
import { LoginPage } from './pages/Login';
import { Forbidden, NotFound } from './pages/Misc';
import { OrderEditorPage } from './pages/orders/OrderEditor';
import { OrdersListPage } from './pages/orders/OrdersList';
import { ProfilePage } from './pages/Profile';
import { ReportsPage } from './pages/Reports';
import { UsersPage } from './pages/Users';

/** Sends signed-out visitors to /login (remembering where they were going). */
function RequireAuth({ children }: { children: ReactElement }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" role="status" aria-label="Loading" /></div>;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

/** The API enforces permissions; this just avoids showing screens that would only return 403. */
function Guard({ menu, children }: { menu: string; children: ReactElement }) {
  const { can } = useAuth();
  return can(menu, 'view') ? children : <Forbidden />;
}

/**
 * Every masters/transactions route renders `<CrudScreen>` with a different `config`. Since it is the
 * same component type at the same position in the tree, navigating between two such routes updates
 * props in place rather than unmounting — but each config's `useTable`/`useForm` calls a different
 * number of hooks, which corrupts React's hook state instead of erroring cleanly the first time.
 * `key={config.endpoint}` forces a full remount whenever the screen actually changes.
 */
const crud = (menu: string, config: Parameters<typeof CrudScreen>[0]['config']) => (
  <Guard menu={menu}><CrudScreen key={config.endpoint} config={config} /></Guard>
);

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<DashboardPage />} />

        <Route path="orders" element={<Guard menu="orders"><OrdersListPage /></Guard>} />
        <Route path="orders/new" element={<Guard menu="orders"><OrderEditorPage /></Guard>} />
        <Route path="orders/:id" element={<Guard menu="orders"><OrderEditorPage /></Guard>} />

        <Route path="accounts/receipt" element={crud('receipt', receiptsConfig)} />
        {/* renamed 2026-10-13: the old address still lands on the Receipt screen */}
        <Route path="accounts/payment-received" element={<Navigate to="/accounts/receipt" replace />} />
        <Route path="accounts/purchase" element={crud('purchase', purchasesConfig)} />
        {/* renamed 2026-10-13: the old address still lands on the Purchases screen */}
        <Route path="accounts/purchase-orders" element={<Navigate to="/accounts/purchase" replace />} />
        <Route path="accounts/payment" element={crud('payment', settlementsConfig)} />
        {/* renamed 2026-10-13: the old address still lands on the Payment screen */}
        <Route path="accounts/payment-made" element={<Navigate to="/accounts/payment" replace />} />
        <Route path="accounts/expenses" element={crud('expenses', expensesConfig)} />
        <Route path="accounts/fund-transfer" element={crud('fund-transfer', fundTransfersConfig)} />
        {/* replaced 2026-10-16: the two old screens (Dispatcher Transactions, Dispatcher Payment) are now one Fund Transfer screen */}
        <Route path="accounts/supplier-transfers" element={<Navigate to="/accounts/fund-transfer" replace />} />
        <Route path="accounts/dispatcher-payments" element={<Navigate to="/accounts/fund-transfer" replace />} />

        <Route path="masters/currencies" element={crud('currencies', currenciesConfig)} />
        <Route path="masters/exchange-rates" element={crud('exchange-rates', exchangeRatesConfig)} />
        <Route path="masters/banks" element={crud('banks', banksConfig)} />
        <Route path="masters/purchasers" element={crud('purchasers', sellersConfig)} />
        <Route path="masters/suppliers" element={crud('suppliers', dispatchersConfig)} />
        <Route path="masters/customers" element={crud('customers', customersConfig)} />
        <Route path="masters/agents" element={crud('agents', agentsConfig)} />

        <Route path="reports" element={<Guard menu="reports"><ReportsPage /></Guard>} />
        <Route path="settings/users" element={<Guard menu="users"><UsersPage /></Guard>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
